import React from 'react';
import PropTypes from 'prop-types';
import { fromGlobalId } from 'graphql-relay';
import get from 'lodash/get';
import GoogleMapReact from 'google-map-react';
import { fitBounds } from 'google-map-react/utils';
import { FormattedMessage, injectIntl } from 'react-intl';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import HighSchoolDetailsDisplay from '../HighSchoolDetailsDisplay';
import messages from './messages';
import './styles.css';

function hasLocation(highSchool) {
  return get(highSchool, 'address.field_hs_elna_location_latlon.latitude') &&
    get(highSchool, 'address.field_hs_elna_location_latlon.latitude');
}

function calculateCenterAndZoom(props, size) {
  const addresses = props.viewer.repVisitHighSchoolsWithinProximity
    .map((distanceWrapper) => distanceWrapper.toSchool)
    .filter((hs) => hasLocation(hs))
    .map((hs) => hs.address);
  if (addresses.length === 1) {
    return {
      center: {
        lat: parseFloat(addresses[0].field_hs_elna_location_latlon.latitude),
        lng: parseFloat(addresses[0].field_hs_elna_location_latlon.longitude),
      },
      zoom: 12,
    };
  }
  const bounds = addresses.reduce((acc, addr) => {
    acc.sw.lat = Math.min(parseFloat(addr.field_hs_elna_location_latlon.latitude), acc.sw.lat || Number.POSITIVE_INFINITY);
    acc.sw.lng = Math.min(parseFloat(addr.field_hs_elna_location_latlon.longitude), acc.sw.lng || Number.POSITIVE_INFINITY);
    acc.ne.lat = Math.max(parseFloat(addr.field_hs_elna_location_latlon.latitude), acc.ne.lat || Number.NEGATIVE_INFINITY);
    acc.ne.lng = Math.max(parseFloat(addr.field_hs_elna_location_latlon.longitude), acc.ne.lng || Number.NEGATIVE_INFINITY);
    return acc;
  }, { sw: {}, ne: {} });
  const { center, zoom } = fitBounds(bounds, { width: size.width - 60, height: size.height - 40 });
  return { center, zoom };
}

class MapView extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      size: null,
      center: null,
      zoom: 12,
      selectedHsId: null,
      hidingOtherHighSchools: true,
      highSchools: [],
    };
    const highSchools = props.viewer.repVisitHighSchoolsWithinProximity
      .map((distanceWrapper) => distanceWrapper.toSchool)
      .filter((hs) => hasLocation(hs));
    if (highSchools.length) {
      const firstHsAddr = highSchools[0].address;
      this.state.center = {
        lat: parseFloat(firstHsAddr.field_hs_elna_location_latlon.latitude),
        lng: parseFloat(firstHsAddr.field_hs_elna_location_latlon.longitude),
      };
    }
  }

  componentDidMount() {
    this.prepareHighSchools();
  }

  componentWillReceiveProps(nextProps) {
    const repDiff = nextProps.viewer.repVisitHighSchoolsWithinProximity !== this.props.viewer.repVisitHighSchoolsWithinProximity;
    const nonRepDiff = nextProps.viewer.nonRepVisitHighSchoolsWithinProximity !== this.props.viewer.nonRepVisitHighSchoolsWithinProximity;
    const resultsChanged = repDiff || nonRepDiff;
    if (!resultsChanged) return;
    this.prepareHighSchools();
    const centerAndZoom = calculateCenterAndZoom(nextProps, this.state.size);
    this.setState(centerAndZoom);
  }

  prepareHighSchools = () => {
    const repVisitsHighSchools = this.props.viewer.repVisitHighSchoolsWithinProximity
      .map((distanceWrapper) => distanceWrapper.toSchool)
      .map((hs, i) => ({ first: i === 0, hs }))
      .filter((item) => hasLocation(item.hs) && this.isRepVisitsHighSchool(item.hs));
    const nonRepVisitsHighSchools = this.props.viewer.nonRepVisitHighSchoolsWithinProximity
      .map((distanceWrapper) => distanceWrapper.toSchool)
      .map((hs) => ({ hs }))
      .filter((item) => hasLocation(item.hs) && !this.isRepVisitsHighSchool(item.hs));
    const highSchools = [...repVisitsHighSchools, ...nonRepVisitsHighSchools];
    this.setState({ highSchools });
  }

  isRepVisitsHighSchool = (highSchool) => {
    const supportsRepVisits = get(highSchool, 'repVisitsAccount.supportsRepVisits', false);
    const supportsCollegeFairs = get(highSchool, 'repVisitsAccount.supportsCollegeFairs', false);
    return supportsRepVisits || supportsCollegeFairs;
  }

  onMapContainerDidMount = (div) => {
    if (div) {
      const size = div.getBoundingClientRect();
      const nextState = { size };
      if (this.props.viewer.repVisitHighSchoolsWithinProximity && this.props.viewer.repVisitHighSchoolsWithinProximity.length) {
        Object.assign(nextState, calculateCenterAndZoom(this.props, size));
      }
      this.setState(nextState);
    } else {
      this.setState({ size: null });
    }
  }

  onChange = (event) => {
    this.setState({ center: { lat: event.center.lat, lng: event.center.lng }, zoom: event.zoom });
  }

  showDetails(hsId) {
    this.setState({ selectedHsId: hsId });
  }

  closeDetails = (event) => {
    this.setState({ selectedHsId: null });
  }

  renderIconsDescription = () => (
    <div styleName="icon-description-container">
      <div styleName="icon-descriptions">
        <div styleName="icon-description active">
          <Icon name="map marker alternate" size="large" styleName="icon" />
          <div styleName="icon-description-label">
            <FormattedMessage {...messages.markerDescriptionActive} />
          </div>
        </div>
        <div styleName="icon-description rep-visits">
          <Icon name="map marker alternate" size="large" styleName="icon" />
          <div styleName="icon-description-label">
            <FormattedMessage {...messages.markerDescriptionRepVisits} />
          </div>
        </div>
        <div styleName="icon-description non-rep-visits">
          <Icon name="map marker alternate" size="large" styleName="icon" />
          <div styleName="icon-description-label">
            <FormattedMessage {...messages.markerDescriptionOther} />
          </div>
          <button data-cy="toggleOtherHighSchools" type="button" styleName="icon-description-button" onClick={() => this.setState((previous) => ({ hidingOtherHighSchools: !previous.hidingOtherHighSchools }))}>
            <FormattedMessage {...(this.state.hidingOtherHighSchools ? messages.actionShowOther : messages.actionHideOther)} />
          </button>
        </div>
      </div>
    </div>
  );

  render() {
    const { hidingOtherHighSchools } = this.state;
    let { highSchools } = this.state;
    if (hidingOtherHighSchools) highSchools = highSchools.filter(({ hs }) => this.isRepVisitsHighSchool(hs));
    return (
      <div styleName="map-container" ref={this.onMapContainerDidMount}>
        {!!highSchools.length && this.renderIconsDescription()}
        {highSchools.length ? (
          <GoogleMapReact
            center={this.state.center}
            zoom={this.state.zoom}
            onChange={this.onChange}
            bootstrapURLKeys={{ key: process.env.GOOGLE_API_KEY }}
          >
            {highSchools.map((item, i) =>
              (

                <div
                  lat={item.hs.address.field_hs_elna_location_latlon.latitude}
                  lng={item.hs.address.field_hs_elna_location_latlon.longitude}
                  key={item.hs.id}
                  styleName="container"
                  style={{ zIndex: 100 - i }}
                >
                  <button type="button" onClick={() => this.showDetails(item.hs.id)} styleName={item.first ? 'marker active' : `marker ${(!this.isRepVisitsHighSchool(item.hs) || '') && 'non-rep-visits'}`}>
                    <Icon name="map marker alternate" size="big" styleName="icon" id="map-marker" data-cy={`map-marker-${item.hs.name}`} />
                    <label styleName="label" htmlFor="map-marker">{item.hs.name}</label>
                  </button>
                  <Modal
                    open={this.state.selectedHsId === item.hs.id}
                    onClose={this.closeDetails}
                    basic
                    closeIcon={<Icon name="close" styleName="details-modal-close" />}
                    styleName="details-modal"
                  >
                    <HighSchoolDetailsDisplay id={fromGlobalId(item.hs.id).id} isNonRepVisits={!this.isRepVisitsHighSchool(item.hs)} />
                  </Modal>
                </div>
              ))}
          </GoogleMapReact>
        ) : null}
      </div>
    );
  }
}

export default injectIntl(MapView);
